import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AppState, TrainerDashboardData } from '../appState/appInitialState';
import type {
  Gym,
  GymProduct,
  Manager,
  Trainer,
  Member,
  Group,
  Program,
  TrainingRoutineTemplate,
  Exercise,
} from '../types';

const getEmptyGymDashboardData = () => ({
  gym: null,
  gymProducts: [],
  managers: {},
  trainers: {},
  unassignedMembers: {},
});

export interface SessionDataContextProps {
  loginManager: Manager | null;
  loginTrainer: Trainer | null;
  loginMember: Member | null;
  gym: Gym | null;
  gymProducts: GymProduct[];
  managers: Record<string, Manager>;
  trainers: Record<string, TrainerDashboardData>;
  members: Record<string, Member>;
  unassignedMembers: Record<string, Member>;
  groups: Record<string, Group>;
  personalizedExercises: Record<string, Exercise>;
  templates: Record<string, TrainingRoutineTemplate>;
  programs: Record<string, Program>;
  gyms: Gym[];
  mergedExercises: Exercise[];
  mergedExercisesMap: Record<string, Exercise>;
}

export const SessionDataContext = createContext<SessionDataContextProps>({
  loginManager: null,
  loginTrainer: null,
  loginMember: null,
  gym: null,
  gymProducts: [],
  managers: {},
  trainers: {},
  members: {},
  unassignedMembers: {},
  groups: {},
  personalizedExercises: {},
  templates: {},
  programs: {},
  gyms: [],
  mergedExercises: [],
  mergedExercisesMap: {},
});

export const SessionDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const selectedGymId = useSelector((state: AppState) => state.selectedGymId);
  const loginManagerId = useSelector((state: AppState) => state.loginManagerId);
  const loginTrainerId = useSelector((state: AppState) => state.loginTrainerId);
  const loginMemberId = useSelector((state: AppState) => state.loginMemberId);
  const gymDataMap = useSelector((state: AppState) => state.gymDataMap);
  const globalExercises = useSelector((state: AppState) => state.exercises);
  const { gym, gymProducts, managers, trainers, unassignedMembers } =
    gymDataMap[selectedGymId ?? ''] || getEmptyGymDashboardData();
  const gyms: Gym[] = Object.values(gymDataMap)
    .map((dashboard) => dashboard.gym)
    .filter((value): value is Gym => Boolean(value));

  const loginManager = loginManagerId ? managers[loginManagerId] : null;
  const loginTrainer = loginTrainerId
    ? trainers[loginTrainerId]?.trainer
    : null;

  const membersFromTrainers: Record<string, Member> = Object.values(
    trainers,
  ).reduce((acc, trainer) => {
    return {
      ...acc,
      ...trainer.members,
    };
  }, {});
  const members: Record<string, Member> = {
    ...membersFromTrainers,
    ...unassignedMembers,
  };
  const loginMember = loginMemberId ? members[loginMemberId] : null;

  const groups: Record<string, Group> = Object.values(trainers).reduce(
    (acc, trainer) => {
      return {
        ...acc,
        ...trainer.groups,
      };
    },
    {},
  );

  const personalizedExercises: Record<string, Exercise> = Object.values(
    trainers,
  ).reduce((acc, trainer) => {
    return {
      ...acc,
      ...trainer.personalizedExercises,
    };
  }, {});

  const templates: Record<string, TrainingRoutineTemplate> = Object.values(
    trainers,
  ).reduce((acc, trainer) => {
    return {
      ...acc,
      ...trainer.templates,
    };
  }, {});

  const programs: Record<string, Program> = Object.values(trainers).reduce(
    (acc, trainer) => {
      return {
        ...acc,
        ...trainer.programs,
      };
    },
    {},
  );

  const sortByName = (a: Exercise, b: Exercise) =>
    (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());

  const personalizedExercisesList = useMemo(
    () =>
      Object.values(trainers)
        .flatMap((trainer) => Object.values(trainer.personalizedExercises))
        .sort(sortByName),
    [trainers],
  );

  const globalExercisesList = useMemo(
    () => Object.values(globalExercises).sort(sortByName),
    [globalExercises],
  );

  const allExercisesList = useMemo(() => {
    const map = new Map<string, Exercise>();
    globalExercisesList.forEach((ex) => {
      if (ex?.id) map.set(ex.id, ex);
    });
    personalizedExercisesList.forEach((ex) => {
      if (ex?.id) map.set(ex.id, ex);
    });
    return Array.from(map.values()).sort(sortByName);
  }, [personalizedExercisesList, globalExercisesList]);

  const mergedExercises = useMemo(
    () => allExercisesList.filter(({ isDeleted }) => !isDeleted),
    [allExercisesList],
  );

  const mergedExercisesMap = useMemo(
    () =>
      allExercisesList.reduce<Record<string, Exercise>>((acc, exercise) => {
        if (exercise?.id) {
          acc[exercise.id] = exercise;
        }
        return acc;
      }, {}),
    [allExercisesList],
  );

  return (
    <SessionDataContext.Provider
      value={{
        loginManager,
        loginTrainer,
        loginMember,
        gym,
        gymProducts,
        managers,
        trainers,
        members,
        unassignedMembers,
        groups,
        personalizedExercises,
        templates,
        programs,
        gyms,
        mergedExercises,
        mergedExercisesMap,
      }}
    >
      {children}
    </SessionDataContext.Provider>
  );
};

export const useSessionData = () => {
  const context = useContext(SessionDataContext);
  if (!context) {
    throw new Error('useSessionData must be used within a SessionDataProvider');
  }
  return context;
};
